import { Tile } from "./tile";
import type { Rng } from "./rng";
import type { Position, SerializedCells, SerializedGrid } from "./types";

/**
 * The board: a `size × size` matrix of cells indexed `[x][y]` (column-major),
 * holding a {@link Tile} or `null`. Ported faithfully from the original 2048
 * `Grid`, with the one behavioural change that random selection draws from an
 * injected {@link Rng} instead of `Math.random` (so games are reproducible).
 */
export class Grid {
  readonly size: number;
  readonly cells: (Tile | null)[][];

  constructor(size: number, previousState?: SerializedCells) {
    this.size = size;
    this.cells = previousState ? this.fromState(previousState) : this.empty();
  }

  private empty(): (Tile | null)[][] {
    const cells: (Tile | null)[][] = [];
    for (let x = 0; x < this.size; x++) {
      const row: (Tile | null)[] = [];
      for (let y = 0; y < this.size; y++) row.push(null);
      cells.push(row);
    }
    return cells;
  }

  private fromState(state: SerializedCells): (Tile | null)[][] {
    const cells: (Tile | null)[][] = [];
    for (let x = 0; x < this.size; x++) {
      const row: (Tile | null)[] = [];
      for (let y = 0; y < this.size; y++) {
        const tile = state[x]?.[y] ?? null;
        row.push(tile ? new Tile(tile.position, tile.value) : null);
      }
      cells.push(row);
    }
    return cells;
  }

  /** Pick a random empty cell using the injected RNG (value draw is the caller's). */
  randomAvailableCell(rng: Rng): Position | undefined {
    const cells = this.availableCells();
    if (cells.length) {
      return cells[Math.floor(rng.next() * cells.length)];
    }
    return undefined;
  }

  /** All empty cells, in column-major order (`for x { for y }`). */
  availableCells(): Position[] {
    const cells: Position[] = [];
    this.eachCell((x, y, tile) => {
      if (!tile) cells.push({ x, y });
    });
    return cells;
  }

  eachCell(callback: (x: number, y: number, tile: Tile | null) => void): void {
    for (let x = 0; x < this.size; x++) {
      for (let y = 0; y < this.size; y++) {
        callback(x, y, this.cells[x]?.[y] ?? null);
      }
    }
  }

  cellsAvailable(): boolean {
    return this.availableCells().length > 0;
  }

  cellAvailable(cell: Position): boolean {
    return !this.cellOccupied(cell);
  }

  cellOccupied(cell: Position): boolean {
    return Boolean(this.cellContent(cell));
  }

  cellContent(cell: Position): Tile | null {
    if (this.withinBounds(cell)) {
      return this.cells[cell.x]?.[cell.y] ?? null;
    }
    return null;
  }

  insertTile(tile: Tile): void {
    const row = this.cells[tile.x];
    if (row) row[tile.y] = tile;
  }

  removeTile(tile: Tile): void {
    const row = this.cells[tile.x];
    if (row) row[tile.y] = null;
  }

  withinBounds(position: Position): boolean {
    return (
      position.x >= 0 &&
      position.x < this.size &&
      position.y >= 0 &&
      position.y < this.size
    );
  }

  serialize(): SerializedGrid {
    const cellState: SerializedGrid["cells"] = [];
    for (let x = 0; x < this.size; x++) {
      const row: SerializedGrid["cells"][number] = [];
      for (let y = 0; y < this.size; y++) {
        const tile = this.cells[x]?.[y] ?? null;
        row.push(tile ? tile.serialize() : null);
      }
      cellState.push(row);
    }
    return { size: this.size, cells: cellState };
  }
}
